import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Column rhythm shared by every profile form: one column on phones, two on tablets and three on wide
 * screens (the app sidebar takes a share of the viewport, so 3 columns only start at `xl`).
 * Use `col-span-full` for a field that needs the whole row.
 */
export const FIELD_GRID = 'grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-3';

/** How far back the date pickers of a person's history (birth, joining, terms, experience) reach. */
export const HISTORY_YEARS = 100;

/** A checkbox styled like a field (same height as an input) so it lines up with inputs in a grid row. */
export const BOXED_CHECKBOX_LABEL =
  'flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md border border-input px-3 py-2 text-sm transition-colors has-[[data-state=checked]]:border-primary/40 has-[[data-state=checked]]:bg-accent has-[[data-disabled]]:cursor-not-allowed has-[[data-disabled]]:border-disabled-border has-[[data-disabled]]:bg-disabled has-[[data-disabled]]:text-disabled-foreground';

export interface FormSectionProps {
  title: ReactNode;
  /** Marks the heading with `*`, for a required group that has no field label of its own. */
  required?: boolean;
  /** Right-aligned control in the heading row (a checkbox or a button that belongs to the whole section). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** A titled group of fields: heading with a divider, then the content. */
export const FormSection = ({
  title,
  required = false,
  action,
  children,
  className,
}: FormSectionProps) => (
  <section className={cn('flex flex-col gap-4', className)}>
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b pb-2.5">
      <h3 className="text-sm font-semibold text-foreground">
        {title}
        {required && <span className="text-destructive"> *</span>}
      </h3>
      {action}
    </div>
    {children}
  </section>
);
